import React, { useMemo } from "react";
import _ from "lodash";

import Children from "./Children";

import PageTitle from "../../../components/PageTitle";
import QuestionFrame from "../../../components/QuestionsFrame";
import CheckboxGroup from "../../../components/CheckboxGroup";
import LongFootnote from "../../../components/LongFootnote";
import ConditionalFieldsWrapper from "../../../components/ConditionalFieldsWrapper";
import ClearValues from "../../../components/ClearValues";
import SubHeading from "../../../components/SubHeading";
import RadioGroup from "../../../components/RadioGroup";
import InfoNote from "../../../components/InfoNote";
import QuestionDuration from "../../../components/QuestionDuration";
import { useFormikContext } from "formik";
import { FlatApplciationType } from "../../../types";

const ApplicationDetails: React.FC = () => {
  const { values } = useFormikContext<FlatApplciationType>();

  const furtherViolenceOptions = [
    {
      value: "incident_pys",
      text: "Physically or sexually abused me or the protected person",
    },
    {
      value: "incident_sty",
      text: "Stalked or followed me or the protected person, either online or in person",
      description:
        "When a person repeatedly contacts you, follows you, monitoring what you do online, using technology to track your movements and/or spending without you knowing, or behaves in a way that makes you feel scared, distressed or fear for your safety, loitering near your house, work or places you often go or keep you under surveillance.",
    },
    {
      value: "incident_aep",
      text: "Emotionally or psychologically abused me or the protected person",
      description:
        "This can include telling you they will: take children away or have them taken by others (such as immigration authorities or Child Protection Services); get you deported, threatened your employment or other important relationships. This can also include threats to disclose personal information about you or images held on a phone or device; making someone feel as though no one cares or will help them; taunting someone about sexuality or gender identity, killed, harmed or threatened to kill or harm a pet/animal so as to control, dominate or pressure you, this can include: hitting a pet/animal; threatening to hurt a pet/animal, or removing, withholding or interfering with a protected person's property, including pets/animals, or threatening to do so.",
    },
    {
      value: "incident_cbp",
      text: "Verbally abused me or the protected person",
      description:
        "This can include telling you they will: take children away or have them taken by others, such as immigration authorities or Child Protection Services; post or share images held on a phone or device; get you deported; taunting you about your sexuality, gender identity, race or other personal characteristics, or threaten to disclose these against your wishes. This can also include name calling, making you feel guilty or questioning your judgement.",
    },
    {
      value: "incident_cym",
      text: "Economically abused me or the protected person",
      description:
        "This can include controlling your money or access to bank accounts against your will; forcing you to pay or give money to others, or taking your money; stopping you from working; forcing you to claim Centrelink payments; forcing or tricking you to take on debts; forcing you to sign legal documents to set up or run a business; removing, keeping or selling your property without your permission; using dowry or family finance issues to control you.",
    },
    {
      value: "incident_thy",
      text: "Threatened or coerced me or the protected person",
      description:
        "This can include saying they will kill or hurt themselves, or trying to hurt or kill themselves [include lifeline support numbers]. This also can include threats to take children away or to have them taken by others, such as immigration authorities or Child Protection Services; threats to disclose someone's sexuality or gender identity; threats to post or send images held on a device and threats to get someone deported, this may include threatening to choke, strangle or suffocate you or attempting to do so.",
    },
    {
      value: "incident_cdw",
      text: "Controlled or dominated me or the protected person in some other way",
      description:
        "Limited your contact or relationships with friends and family, limited cultural and religious freedom, limited access to mobility aids or medical care., made you do something you did not want to do, forced you to marry without your consent. This can include intimidation, bullying and frightening you or your children; controlling where someone goes, what they wear or eat, when they sleep; using coercive control which involves manipulation and creates fear or concern in someone, denying someone's freedom and ability to make their own decisions.",
    },
    {
      value: "incident_htc",
      text: "Behaved in a way that caused a child to witness or be exposed to the above behaviour",
      description:
        "This can include instances where children have seen or overheard arguing, conversations, verbal or physical violence, or needed to stay elsewhere due to an incident. This can also include the child seeing injuries which were perpetrated by the respondent or the after effects of the family violence. It can include the child comforting or providing assistance to a family member who has been physically abused by another family member; cleaning up a site after a family member has intentionally damaged another family member's property; being present when police officers attend an incident involving physical abuse of a family member by another family member.",
    },
  ];
  let reasonsLabel = "";

  if (_.isEqual(values.app_types, ["AE", "AV"])) {
    furtherViolenceOptions.push({
      value: "incident_fsw",
      text: "Other. Something else has happened, or I am worried something will happen, that warrants an extension and variation to the order.",
    });
    reasonsLabel =
      "What has changed since the order was made that has prompted you to want to extend and vary the order?";
  } else if (_.isEqual(values.app_types, ["AE"])) {
    furtherViolenceOptions.push({
      value: "incident_fsw",
      text: "Other. Something else has happened, or I am worried something will happen, that warrants an extension to the order.",
    });
    reasonsLabel =
      "What has changed since the order was made that has prompted you to want to extend the order?";
  } else if (_.isEqual(values.app_types, ["AV"])) {
    furtherViolenceOptions.push({
      value: "incident_fsw",
      text: "Other. Something else has happened, or I am worried something will happen, that warrants a variation to the order.",
    });
    reasonsLabel =
      "What has changed since the order was made that has prompted you to want to vary the order?";
  }

  // This function generates bullet points based on the current form values
  const bulletPoints = useMemo((): string => {
    const bulletPointsMap: Record<string, any> = {
      incident_pys: {
        value: `- **Physical and/or sexual abuse**\n`,
        children: {
          physical_harm: {
            value: `  - **Physical harm**\n`,
          },
          choking_strangling: {
            value: `  - **Non-fatal strangulation**\n`,
          },
          using_weapons: {
            value: `  - **Use of weapons**\n`,
          },
          sexual_pressure: {
            value: `  - **Pressuring**\n`,
          },
          child_harm: {
            value: `  - **Harm of a child**\n`,
          },
        },
      },
      incident_sty: {
        value: `- **Stalking**\n`,
      },
      incident_aep: {
        value: `- **Emotional and/or psychological abuse**\n`,
      },
      incident_cbp: {
        value: `- **Verbal abuse**\n`,
      },
      incident_cym: {
        value: `- **Economical abuse**\n`,
      },
      incident_thy: {
        value: `- **Threats or coercion**\n`,
        children: {
          threatened_kill_person: {
            value: `  - **Threats to kill a person**\n`,
          },
          threatened_harm_person: {
            value: `  - **Threats to harm a person**\n`,
          },
          threatened_kill_animal: {
            value: `  - **Threats to kill an animal or pet**\n`,
          },
          threatened_self_harm: {
            value: `  - **Threats to harm or kill themselves**\n`,
          },
        },
      },
      incident_cdw: {
        value: `- **Control or domination**\n`,
        children: {
          controlling: {
            value: `  - **Controlling who I could see or speak to**\n`,
          },
        },
      },
      incident_htc: {
        value: `- **Exposing children to family violence**\n`,
      },
    };

    if (
      !values.app_type_extend_further_violence ||
      !_.isArray(values.app_type_extend_further_violence)
    ) {
      return "\n";
    }

    return (values.app_type_extend_further_violence as string[]).reduce(
      (markdown, key) => {
        const item = bulletPointsMap[key];
        if (!item) return markdown;

        let result = markdown + item.value;
        const value = values[key];

        if (item.children && _.isArray(value)) {
          result += (value as string[]).reduce((childMarkdown, childKey) => {
            const childItem = item.children![childKey];
            return childItem ? childMarkdown + childItem.value : childMarkdown;
          }, "");
        }

        return result;
      },
      "",
    );
  }, [values]);

  return (
    <>
      <ConditionalFieldsWrapper
        conditions={{
          app_types: ({ app_types }) => _.isEqual(app_types, ["AE"]),
        }}
      >
        <PageTitle heading="Extend" />
        <SubHeading
          heading="Tell us why you want to extend the order, including any relevant information about your current circumstances."
          description="In considering your application, the court must be satisfied that if the order is not extended the respondent is likely to commit family violence against the protected person. Thinking about the current circumstances, please provide details on why you need the order to be extended and why you believe the respondent may commit family violence against you and/or your children or the protected person, if the order is not extended."
        />
      </ConditionalFieldsWrapper>

      <ConditionalFieldsWrapper
        conditions={{
          app_types: ({ app_types }) => _.isEqual(app_types, ["AV"]),
        }}
      >
        <PageTitle heading="Vary" />
        <SubHeading
          heading="Tell us why you want to vary the conditions on the order, including any relevant information about your current circumstances."
          description="In considering your application, the court must be satisfied that if the order is not varied the respondent is likely to commit family violence against the protected person. Thinking about the current circumstances, please provide details on why you need the order to be varied and why you believe the respondent may commit family violence against you and/or your children or the protected person, if the order is not varied."
        />
      </ConditionalFieldsWrapper>

      <ConditionalFieldsWrapper
        conditions={{
          app_types: ({ app_types }) =>
            _.isEqual(_.sortBy(app_types as string[]), ["AE", "AV"]),
        }}
      >
        <PageTitle heading="Extend and Vary" />
        <SubHeading
          heading="Tell us why you want to extend and vary the conditions on the order, including any relevant information about your current circumstances."
          description="In considering your application, the court must be satisfied that if the order is not extended and varied the respondent is likely to commit family violence against the protected person. Thinking about the current circumstances, please provide details on why you need the order to be extended and varied and why you believe the respondent may commit family violence against you and/or your children or the protected person, if the order is not extended and varied."
        />
      </ConditionalFieldsWrapper>

      <ConditionalFieldsWrapper
        key={bulletPoints}
        conditions={{
          app_types: ["AE", "AV"],
        }}
      >
        <QuestionFrame>
          <RadioGroup
            name="app_type_extend_further_violence_check"
            optionChildren={{
              yes: (
                <>
                  <CheckboxGroup
                    name="app_type_extend_further_violence"
                    options={furtherViolenceOptions}
                    optionChildren={{
                      incident_pys: <CheckboxGroup name="incident_pys" />,
                      incident_thy: <CheckboxGroup name="incident_thy" />,
                      incident_cdw: <CheckboxGroup name="incident_cdw" />,
                      incident_fsw: <LongFootnote name="incident_fsw_detail" />,
                    }}
                  />
                  <InfoNote>
                    <p style={{ marginTop: 0 }}>
                      Many of the behaviours listed above are criminal offences.
                      If you have experienced any of these, you may report this
                      to the police. The police may charge the respondent with
                      these offences.
                    </p>
                  </InfoNote>
                  <LongFootnote
                    name="app_type_extend_why_breached"
                    note={bulletPoints}
                  />
                </>
              ),
              no: (
                <>
                  <ClearValues
                    names={[
                      "app_type_extend_further_violence",
                      "incident_fsw_detail",
                    ]}
                  />
                  <LongFootnote
                    name="app_type_extend_why_violence_stopped"
                    label={reasonsLabel}
                  />
                </>
              ),
            }}
          />
          <RadioGroup
            name="app_type_extend_still_fearful"
            label={values.prp_person === 'no' ? 'On a scale of 1-5 please rate how fearful the protected person(s) are:' : 'On a scale of 1-5 please rate how fearful you are:'}
            displayRows
          />
        </QuestionFrame>
        <QuestionFrame
          privateFrame
          privateHeading="Private question - ||the respondent|Respondent|The person who used family violence against you and/or your children in this application.|| will not see this answer."
        >
          <RadioGroup
            name="appl_pregnant_baby"
            label={values.prp_person === 'no' ? 'Are the protected person(s) pregnant or have a child under 6 months?' : 'Are you pregnant or have a child under 6 months?'}
          />
        </QuestionFrame>
      </ConditionalFieldsWrapper>

      <ConditionalFieldsWrapper
        conditions={{
          app_types: "AE",
        }}
      >
        <QuestionFrame>
          <ConditionalFieldsWrapper
            conditions={{
              app_types: "AV",
            }}
          >
            <h2>Extend details</h2>
          </ConditionalFieldsWrapper>
          <QuestionDuration
            name="app_type_extend_duration"
            units={["Months", "Years"]}
          />
        </QuestionFrame>
      </ConditionalFieldsWrapper>

      <ConditionalFieldsWrapper
        conditions={{
          app_types: "AV",
        }}
      >
        <QuestionFrame>
          <ConditionalFieldsWrapper
            conditions={{
              app_types: "AE",
            }}
          >
            <h2>Vary details</h2>
          </ConditionalFieldsWrapper>
          <RadioGroup
            name="vary_resp_children"
            optionChildren={{
              yes: (
                <CheckboxGroup
                  name="child_action"
                  optionChildren={{
                    add: <Children id="addchild" />,
                    remove: <Children id="removechild" />,
                  }}
                />
              ),
            }}
          />
        </QuestionFrame>
        <SubHeading heading="What conditions do you want to change on the Family Violence Intervention Order?" />
        <QuestionFrame>
          <LongFootnote name="vary_resp_other" charLimit={150} allowNegative short />
        </QuestionFrame>
      </ConditionalFieldsWrapper>

      <ConditionalFieldsWrapper
        conditions={{
          app_types: "AR",
        }}
      >
        <PageTitle
          heading="Revoke"
          description={
            <>
              <p>
                Revoking an intervention order means removing the intervention
                order. If an order is revoked there will no longer be an order
                prohibiting family violence.
              </p>
              <p>In considering your application, the court must consider:</p>
              <ul>
                <li>your reasons for seeking the revocation,</li>
                <li>the safety of the protected person,</li>
                <li>
                  the protected person's views about the variation or
                  revocation,
                </li>
                <li>
                  whether or not the protected person is legally represented,
                  and
                </li>
                <li>
                  if the protected person has a guardian, the guardian's views.
                </li>
              </ul>
              <p>
                If you are not the original applicant to the Family Violence
                Intervention Order. The court will consider the original
                applicants view regarding this application which includes
                Victoria Police. A copy of this application will be provided to
                each party to the proceeding under which the family violence
                intervention order was made.
              </p>
            </>
          }
        />
        <QuestionFrame>
          <LongFootnote name="revoke_appl_why" charLimit={250} allowNegative />
        </QuestionFrame>
      </ConditionalFieldsWrapper>
    </>
  );
};

export default ApplicationDetails;
